import Vue from 'vue'
import { onMounted, onUnmounted, reactive, computed, ref, watch } from '@vue/composition-api'
import data from '@iez/lib/data/'
import user from '@iez/lib/user/'
import { theme } from '@iez/core'

let stripeInit = false
if (!window.Stripe && !stripeInit) {
  stripeInit = true
  const script = document.createElement(`script`)
  script.async = true
  script.src = `https://js.stripe.com/v3/`
  document.body.appendChild(script)
}

export default ({ objs, purchase }) => {
  if (purchase === 0) {
    return {
      state: {
        ready: true,
        ...Object.fromEntries(Object.keys(objs).map(obj => [
          obj,
          {
            disabled: true
          }
        ]))
      }
    }
  }
  const genComputed = (prop, conditionPipe=(condition => condition)) => ({
    [prop]: computed(() => conditionPipe(
      Object.keys(objs).every(obj => state[obj][prop])
    ))
  })
  const state = reactive({
    ...Object.fromEntries(Object.keys(objs).map(obj => [obj, {
      _disabled: true,
      disabled: computed(() => state[obj]._disabled),
      ready: false,
      isEmpty: true,
      value: computed(() => state[obj].isEmpty ? null : obj)
    }])),
    clientSecret: ``,
    error: null,
    ...genComputed('complete', condition => condition),
    ...genComputed('ready', condition => condition && !!state.clientSecret)
  })
  const stripe = window.Stripe(data.config.stripeKey)
  const elements = stripe.elements({
    fonts: [{
      cssSrc: `https://fonts.googleapis.com/css?family=Roboto:100,300,400,500,700,900`
    }]
  })
  const create = ([type, { value: component }]) => {
    const isFocused = ref(false)
    const { $el } = component
    const element = elements.create(type, {
      style: {
        base: {
          fontFamily: 'Roboto'
        }
      },
      placeholder: component.placeholder || ``
    })
    const div = document.createElement('div')
    const input = $el.querySelector('input')
    const css = window.getComputedStyle(input)
    div.style.paddingTop = `${parseInt(css.paddingTop) - 2}px`
    div.style.paddingBottom = css.paddingBottom
    div.style.width = css.width
    div.style.height = css.height
    div.style.marginTop = css.marginTop
    div.style.position = 'absolute'
    const setColor = ({ color }) => {
      element.update({
        style: {
          base: {
            ...Object.fromEntries([
              'color',
              'fontSize',
              'fontStyle',
              'fontVariant',
              'fontWeight',
              'letterSpacing',
              'textAlign',
              'textDecoration',
              'textShadow',
              'textTransform'
            ].map(prop => [
              prop,
              css[prop]
            ])),
            color,
            '::placeholder': {
              color: color === `#000`
                ? `rgba(0, 0, 0, 0.87)`
                : color
            }
          },
        }
      })
    }
    const color = computed(
      () => isFocused.value || !state[type].isEmpty
        ? theme.dark
          ? `#fff`
          : `#000`
        : `transparent`
    )
    watch(
      () => color.value,
      color => {
        setColor({ color })
      }, {
        immediate: true
      }
    )
    // const display = div.style.display
    div.style.display = 'none'
    input.parentNode.appendChild(div)
    element.mount(div)

    const onFocus = () => {
      element.focus()
    }

    element.on('ready', () => {
      state[type]._disabled = false
      state[type].ready = true

      div.style.display = 'block'
      input.style.transition = 'opacity .3s'
      input.style.opacity = 0
      element.on('focus', () => {
        isFocused.value = true
      })
      element.on('blur', () => {
        isFocused.value = false
      })
      watch(
        () => isFocused.value,
        isFocused => {
          component.isFocused = isFocused
        }
      )
      element.on('change', ({ error, complete, empty }) => {
        Vue.set(
          state[type],
          'errorMessages',
          error
            ? [error.message]
            : undefined
        )
        Vue.set(
          state[type],
          `isEmpty`,
          !!empty
        )
        Vue.set(
          state[type],
          'complete',
          !!complete
        )
      })
      input.addEventListener('focus', onFocus)
    })
    return () => {
      element.unmount()
      input.removeEventListener('focus', onFocus)
    }
  }
  const clean = []
  onMounted(() => {
    clean.push(...Object.entries(objs).map(create))
  })
  onUnmounted(() => {
    const copy = clean.slice(0)
    clean.splice(0, clean.length)
    copy.forEach(clean => clean())
  })
  const production = process.env.NODE_ENV !== 'development'
  const urlDefault = `https://us-central1-${data.config.project}.cloudfunctions.net/payment/secret`
  const urlTest = `https://us-central1-${data.config.project}.cloudfunctions.net/paymentTest/secret`
  const url = production
    ? urlDefault
    : urlTest

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ amount: purchase })
  }).then(result => result.json())
  .then(({ result }) => {
    state.clientSecret = result
  })

  const pay = async () => {
    const [
      { id: paymentId },
      { error, paymentIntent }
    ] = await Promise.all([
      data.set({
        root: `payments`,
        fields: [`id`],
        items: [{
          userId: user.id,
          amount: purchase
        }],
        one: true
      }),
      stripe.confirmCardPayment(
        state.clientSecret, {
          payment_method: {
            card: elements.getElement(`cardNumber`)
          }
        }
      )
    ])
    state.error = error ? error.message : null
    if (state.error) {
      console.error(state.error)
    } else {
      await data.update({
        root: `payments`,
        id: paymentId,
        _set: {
          paymentIntentId: paymentIntent.id
        },
        fields: ['id']
      })
    }
    return {
      paymentId,
      error: state.error
    }
  }

  return {
    state,
    pay
  }
}
